import Debounce from '@sd-utility/debounce';
import { IconChevronRight } from '@tvg/fuse-icons';
import {transformer} from '@tvg/fuse-utils';
import PropTypes from 'prop-types';
import React from 'react';
import { Button, SpanButton } from '../../fuse-buttons/src/buttons';
import {
	backgrounds,
	componentConfig,
	headlineSizesV3 as headlineSizesV3Config,
	variants,
} from './image-hero-2-config';
import {
	StyledButtons,
	StyledContentWrapper,
	StyledCopyWrapper,
	StyledDescription,
	StyledHeading,
	StyledImageHeading,
	StyledImageHero,
	StyledImageHeroContainer,
	StyledImageWrapper,
	StyledPicture,
	StyledRepresentative,
	StyledSubheading,
	StyledTerms,
} from './styled-image-hero-2';

const { useCallback, useEffect, useLayoutEffect, useRef } = React;

const { typography: typographyConfig } = componentConfig;

const defaultProps = {
	background: 'white',
	dataTestId: 'fuse-image-hero-2-test-id',
	dataAnalyticsId: undefined,
	dataAnalyticsIdSecondary: undefined,
	image: {
		fetchPriority: undefined,
		isLazy: true,
		position: 'right',
		ratio: '1:1',
	},
	link: {
		href: undefined,
		onClick: undefined,
		text: undefined,
	},
	secondaryLink: {
		href: undefined,
		onClick: undefined,
		text: undefined,
	},
	text: {
		description: undefined,
		heading: undefined,
		headline: undefined,
		headlineSizeV3: 'small',
		headlineTag: 'h2',
		representative: undefined,
		representativeColour: 'brand',
		subheadline: undefined,
		subheadlineTag: 'h3',
		terms: undefined,
	},
	variant: variants.v2,
};

const propTypes = {
	background: PropTypes.oneOf(backgrounds),
	dataAnalyticsId: PropTypes.string,
	dataTestId: PropTypes.string,
	dataAnalyticsIdSecondary: PropTypes.string,
	image: PropTypes.shape({
		alt: PropTypes.string.isRequired,
		fetchPriority: PropTypes.oneOf(['auto', 'high', 'low']),
		isLazy: PropTypes.bool,
		fallbackSource: PropTypes.string.isRequired,
		position: PropTypes.oneOf(['left', 'right']),
		ratio: PropTypes.oneOf(['1:1', '4:3']),
		sources: PropTypes.arrayOf(
			PropTypes.shape({
				srcSet: PropTypes.string.isRequired,
				type: PropTypes.string.isRequired,
			}),
		),
	}),
	link: PropTypes.shape({
		href: PropTypes.string,
		onClick: PropTypes.func,
		text: PropTypes.string,
	}),
	secondaryLink: PropTypes.shape({
		href: PropTypes.string,
		onClick: PropTypes.func,
		text: PropTypes.string,
	}),
	text: PropTypes.shape({
		description: PropTypes.string,
		headline: PropTypes.string,
		headlineSizeV3: PropTypes.oneOf(Object.keys(headlineSizesV3Config)), // xsmall, small, medium, large
		headlineTag: PropTypes.string,
		representative: PropTypes.string,
		representativeColour: PropTypes.oneOf(['brand', 'grey']),
		subheadline: PropTypes.string,
		subheadlineTag: PropTypes.string,
		terms: PropTypes.string,
	}),
	variant: PropTypes.oneOf([variants.v2, variants.v3]),
};

/**
 * For v2, Function returns mobileHeadlineSize, tabletHeadlineSize and desktopHeadlineSize based on the character count passed
 * For v3, Function returns mobileHeadlineSize, tabletHeadlineSize and desktopHeadlineSize based on the headlineSizeV3 prop passed i.e. xsmall, small, medium, large
 * @param {number} headlineCharacterCount - Total number of characters of the Image Hero Heading
 * @returns {array} [mobileHeadlineSize, tabletHeadlineSize, desktopHeadlineSize]
 */

const getHeadlineSize = (headlineCharacterCount, { isV3, hasRepresentative, headlineSizeV3 }) => {
	// Sets headline size for V3
	if (isV3) {
		// Get representative text sizes from config
		const aprHeadlineSizes = [
			typographyConfig.size.representative.xxs[variants.v3],
			typographyConfig.size.representative.m[variants.v3],
			typographyConfig.size.representative.l[variants.v3],
		];

		// Set headline size based on representative text size from config when rep text is set
		const mobileHeadlineSize = headlineSizesV3Config[headlineSizeV3].xxs;
		const tabletHeadlineSize = headlineSizesV3Config[headlineSizeV3].m;
		const desktopHeadlineSize = headlineSizesV3Config[headlineSizeV3].l;

		return hasRepresentative
			? aprHeadlineSizes
			: [mobileHeadlineSize, tabletHeadlineSize, desktopHeadlineSize];
	}

	// Sets headline size for V2
	if (headlineCharacterCount < 16) return [2, 6, 11];
	if (headlineCharacterCount > 15 && headlineCharacterCount < 30) return [2, 5, 9];
	if (headlineCharacterCount > 29 && headlineCharacterCount < 45) return [2, 4, 8];

	return [2, 4, 8];
};

function ImageHero2({
	background,
	image,
	text,
	link,
	secondaryLink,
	dataAnalyticsId,
	dataTestId,
	dataAnalyticsIdSecondary,
	variant,
	...props
}) {
	const sanitisedBackground = backgrounds.includes(background) ? background : 'white';
	const sanitisedImagePosition = ['left', 'right'].includes(image?.position)
		? image.position
		: 'right';
	const sanitisedImageRatio = ['1:1', '4:3'].includes(image?.ratio) ? image.ratio : '1:1';
	const sanitisedHeadlineSizeV3 = Object.keys(headlineSizesV3Config).includes(text?.headlineSizeV3)
		? text?.headlineSizeV3
		: 'small';
	const sanitisedVariant = [variants.v2, variants.v3].includes(variant) ? variant : variants.v2;
	const isV3 = sanitisedVariant === variants.v3;
	const wrapSymbolHeadline = transformer(text.headline);
	const wrapSymbolSubHeadline = transformer(text.subheadline);
	const wrapSymbolDescription = transformer(text.description);

	const headlineCharacterCount = text.headline ? text.headline.length : 0;
	const [mobileHeadlineSize, tabletHeadlineSize, desktopHeadlineSize] = getHeadlineSize(
		headlineCharacterCount,
		{ isV3, hasRepresentative: !!text.representative, headlineSizeV3: sanitisedHeadlineSizeV3 },
	);
	const { ...pictureProps } = image;
	const activePrimaryCTA = link.text && (link.href || link.onClick);
	const activeSecondaryCTA = secondaryLink.text && (secondaryLink.href || secondaryLink.onClick);
	// Remove content wrapper padding based on the condition below
	const noMobileContentPadding =
		(variant === variants.v3 &&
			!text.subheadline &&
			!text.description &&
			!text.representative &&
			!text.terms &&
			!activePrimaryCTA &&
			!activeSecondaryCTA) ||
		(variant === variants.v2 &&
			!text.headline &&
			!text.subheadline &&
			!text.description &&
			!text.representative &&
			!text.terms &&
			!activePrimaryCTA &&
			!activeSecondaryCTA);
	// Configs
	// Text Config
	const { size: textSize, type: textType } = typographyConfig;
	// Refs
	const copyWrapperRef = useRef();
	const buttonsContainerRef = useRef();
	const primaryButtonRef = useRef();
	const secondaryButtonRef = useRef();
	/**
	 * Function that sets the layout of the buttons to side by side or stacked depending on the buttons width, gap, container size and viewport
	 */
	const setButtonsLayout = useCallback(() => {
		// Button gap set to initial side by side gap of --spacing-global-space6 = 16px
		const buttonGap = 16;
		const copyWrapperWidth = Math.floor(copyWrapperRef.current.offsetWidth) - 8; // Round down and adding some margins to account for button borders + extra spacing when total button widths calculation is close to copy wrapper width
		const primaryButtonClone = primaryButtonRef.current.cloneNode(true);
		const secondaryButtonClone = secondaryButtonRef.current.cloneNode(true);

		/**
		 * Calculate actual widths of buttons by setting width to auto from 100% of container when stacked
		 * Using cloned buttons for calculation removes chance of 'jank' as its not visible when appended and removed from DOM due to style set
		 */
		Object.assign(primaryButtonClone.style, {
			width: 'auto',
			position: 'absolute',
			visibility: 'hidden',
		});

		Object.assign(secondaryButtonClone.style, {
			width: 'auto',
			position: 'absolute',
			visibility: 'hidden',
		});

		buttonsContainerRef.current.appendChild(primaryButtonClone);
		buttonsContainerRef.current.appendChild(secondaryButtonClone);

		// Get primary and secondary button widths
		const primaryButtonWidth = primaryButtonClone.offsetWidth;
		const secondaryButtonWidth = secondaryButtonClone.offsetWidth;
		const totalButtonsWidth = Math.ceil(primaryButtonWidth + buttonGap + secondaryButtonWidth); // Round up value so to mitigate edge cases

		buttonsContainerRef?.current.removeChild(primaryButtonClone);
		buttonsContainerRef?.current.removeChild(secondaryButtonClone);

		// Update button styles to stacked or side by side based on total button widths
		if (
			totalButtonsWidth >= copyWrapperWidth &&
			!buttonsContainerRef.current.classList.contains('fuse-image-hero-2__buttons--stacked')
		) {
			buttonsContainerRef.current.classList.add('fuse-image-hero-2__buttons--stacked');
			primaryButtonRef.current.classList.add('fuse-image-hero-2__primary-button--stacked');
			secondaryButtonRef.current.classList.add('fuse-image-hero-2__secondary-button--stacked');
		}

		if (
			totalButtonsWidth < copyWrapperWidth &&
			buttonsContainerRef.current.classList.contains('fuse-image-hero-2__buttons--stacked')
		) {
			buttonsContainerRef.current.classList.remove('fuse-image-hero-2__buttons--stacked');
			primaryButtonRef.current.classList.remove('fuse-image-hero-2__primary-button--stacked');
			secondaryButtonRef.current.classList.remove('fuse-image-hero-2__secondary-button--stacked');
		}
	}, [link.text, secondaryLink.text]);

	/**
	 * Add resize event listener (set buttons layout) to window for when user changes orientation or viewport size
	 */
	useEffect(() => {
		if (activePrimaryCTA && activeSecondaryCTA) {
			const handleResize = Debounce(() => {
				setButtonsLayout();
			}, 200);

			window.addEventListener('resize', handleResize);

			return () => {
				window.removeEventListener('resize', handleResize);
			};
		}
		return () => {};
	}, []);


	/**
	 * Using useLayoutEffect to remove 'jank' of button growing to the available space when stacked ie execute effects first before rendering
	 */
	useLayoutEffect(() => {
		if (activePrimaryCTA && activeSecondaryCTA) setButtonsLayout();
	}, [link.text, secondaryLink.text]);

	return (
		<StyledImageHero
			as={activePrimaryCTA && !activeSecondaryCTA && 'a'}
			background={sanitisedBackground}
			href={link.href}
			data-analyticsid={activePrimaryCTA && !activeSecondaryCTA && dataAnalyticsId}
			data-testid={dataTestId}
			onClick={!link.href && !activeSecondaryCTA ? link.onClick : undefined}
			{...props}
		>
			<StyledImageHeroContainer imagePosition={sanitisedImagePosition}>
				<StyledImageWrapper variant={sanitisedVariant} imageRatio={sanitisedImageRatio}>
					{text.headline && isV3 && (
						<StyledImageHeading
							size={{
								xxs: `${mobileHeadlineSize}`,
								m: `${tabletHeadlineSize}`,
								l: `${desktopHeadlineSize}`,
							}}
							tag={text.headlineTag}
							type={textType.headline}
							variant={sanitisedVariant}
							dangerouslySetInnerHTML={{ __html: wrapSymbolHeadline }}
						/>
					)}
					<StyledPicture
						className="fuse-image-hero-2__picture"
						{...pictureProps}
						variant={sanitisedVariant}
						imageRatio={sanitisedImageRatio}
					/>
				</StyledImageWrapper>
				<StyledContentWrapper
					imagePosition={sanitisedImagePosition}
					noMobileContentPadding={noMobileContentPadding}
					variant={sanitisedVariant}
				>
					<StyledCopyWrapper ref={copyWrapperRef}>
						{text.headline && (
							<StyledHeading
								size={{
									xxs: `${mobileHeadlineSize}`,
									m: `${tabletHeadlineSize}`,
									l: `${desktopHeadlineSize}`,
								}}
								tag={text.headlineTag}
								type={textType.headline}
								variant={sanitisedVariant}
								dangerouslySetInnerHTML={{ __html: wrapSymbolHeadline }}
							/>
						)}
						{text.subheadline && (
							<StyledSubheading
								size={{
									xxs: textSize.subHeadline.xxs[sanitisedVariant],
									m: textSize.subHeadline.m[sanitisedVariant],
									l: textSize.subHeadline.l[sanitisedVariant],
								}}
								tag={text.subheadlineTag}
								type={textType.subHeadline}
								dangerouslySetInnerHTML={{ __html: wrapSymbolSubHeadline }}
							/>
						)}
						{text.description && (
							<StyledDescription
								size={{
									xxs: textSize.description.xxs[sanitisedVariant],
									l: textSize.description.l[sanitisedVariant],
								}}
								tag="p"
								type={textType.description}
								dangerouslySetInnerHTML={{ __html: wrapSymbolDescription }}
							/>
						)}
						{text.representative && (
							<StyledRepresentative
								background={sanitisedBackground}
								size={{
									xxs: textSize.representative.xxs[sanitisedVariant],
									l: textSize.representative.l[sanitisedVariant],
								}}
								tag="p"
								type={textType.representative}
								representativeColour={text.representativeColour}
							>
								{transformer(text.representative)}
							</StyledRepresentative>
						)}
						{text.terms && (
							<StyledTerms
								size={{
									xxs: textSize.terms.xxs[sanitisedVariant],
								}}
								tag="p"
								type={textType.terms}
								dangerouslySetInnerHTML={{ __html: text.terms }}
							/>
						)}
					</StyledCopyWrapper>
					{activePrimaryCTA && (
						<StyledButtons ref={buttonsContainerRef}>
							{activeSecondaryCTA ? (
								<>
									<Button
										{...link}
										className="fuse-image-hero-2__primary-button"
										data-analyticsid={dataAnalyticsId}
										data-testid="fuse-image-hero-2-primary-button-test-id"
										href={link.href}
										icon={<IconChevronRight />}
										iconRight
										onClick={link.onClick}
										ref={primaryButtonRef}
										size={{ xxs: 'small', m: 'medium', l: 'large' }}
										variant="secondary"
									>
										{link.text}
									</Button>
									<Button
										{...secondaryLink}
										className="fuse-image-hero-2__secondary-button"
										data-analyticsid={dataAnalyticsIdSecondary}
										data-testid="fuse-image-hero-2-secondary-button-test-id"
										href={secondaryLink.href}
										onClick={secondaryLink.onClick}
										ref={secondaryButtonRef}
										size={{ xxs: 'small', m: 'medium', l: 'large' }}
										variant="tertiary"
									>
										{secondaryLink.text}
									</Button>
								</>
							) : (
								<SpanButton
									{...link}
									className="fuse-image-hero-2__span-button"
									data-testid="fuse-image-hero-2-span-button-test-id"
									icon={<IconChevronRight />}
									iconRight
									size={{ xxs: 'small', m: 'medium', l: 'large' }}
									variant="secondary"
								>
									{link.text}
								</SpanButton>
							)}
						</StyledButtons>
					)}
				</StyledContentWrapper>
			</StyledImageHeroContainer>
		</StyledImageHero>
	);
}

ImageHero2.propTypes = propTypes;
ImageHero2.defaultProps = defaultProps;

export { ImageHero2 };
export default ImageHero2;
